import React, { useState } from 'react';

import { useHistory } from 'react-router';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell, { TableCellProps } from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { ACCOUNT_PAGE_SIZE } from '../admin/components/pending-admin-action-tabs/account-table-pagination';

import { AccountItemMenu } from '../admin/account-item-menu';
import { UiSortLabel } from '../ui/ui-sort-label';
import { orgRoute } from '../web-routes';

import { ACCOUNT_LIST_BASIC_COLUMNS } from './accounts-list-columns';
import { AccountsWithScore } from './accounts-list-page';

import {
  SORT_ORDER,
  sortAlphabetically,
  sortByDate,
  sortByNullAwareField,
} from '../util/sort-functions';

type SortKey = keyof AccountsWithScore;

export type ColumnComponentProps = {
  account: AccountsWithScore;
};

export type AccountsListColumn = {
  label: string;
  value?: SortKey;
  ColumnComponent: React.FC<ColumnComponentProps>;
  isAdminOnly?: boolean;
  hasDateValue?: boolean;
  disableSorting?: boolean;
  columnAlign?: TableCellProps['align'];
};

export interface AccountsListTableProps {
  accounts: AccountsWithScore[];
  columns?: AccountsListColumn[];
  defaultSortBy?: 'firstHistoryEntry' | 'accountName';
  showAccountActions?: boolean;
  paginationOffset?: number;
  navigateToAccountOnRowClick?: boolean;
}

export const AccountsListTable = ({
  accounts,
  columns = ACCOUNT_LIST_BASIC_COLUMNS,
  defaultSortBy = 'accountName',
  showAccountActions = false,
  paginationOffset,
  navigateToAccountOnRowClick = true,
}: AccountsListTableProps) => {
  const history = useHistory();
  const [sortBy, setSortBy] = useState<SortKey>(defaultSortBy);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleAccountClick = (orgArray: TeamEngineAccountOrg[]) => () => {
    if (!navigateToAccountOnRowClick) {
      return;
    }
    const sortedOrgs = orgArray
      .filter((o) => o.isActive)
      .sort(sortAlphabetically((o) => o.name, SORT_ORDER.ASC));
    const [firstListedOrg] = sortedOrgs;
    if (firstListedOrg) {
      history.push(orgRoute(firstListedOrg.org_id));
    }
  };

  const handleSortByChange = (newSortBy: SortKey) => {
    setSortBy(newSortBy);
    setSortDirection('asc');
  };

  let sortedAccounts = accounts;
  const [column] = columns.filter((c) => c.value === sortBy);
  if (column.hasDateValue) {
    sortedAccounts = sortedAccounts.sort(sortByDate((account) => account[sortBy]));
  } else {
    sortedAccounts = sortedAccounts.sort(sortByNullAwareField(sortBy, sortDirection));
  }
  if (sortDirection === 'desc' && column.hasDateValue) {
    sortedAccounts.reverse();
  }

  if (typeof paginationOffset === 'number' && sortedAccounts.length > ACCOUNT_PAGE_SIZE) {
    sortedAccounts = sortedAccounts.slice(paginationOffset, paginationOffset + ACCOUNT_PAGE_SIZE);
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          {columns.map((h) => {
            if (h.disableSorting) {
              return <TableCell key={h.value}>{h.label}</TableCell>;
            }
            return (
              <TableCell key={h.value}>
                {h.value ? (
                  <UiSortLabel
                    label={h.label}
                    sortKey={h.value}
                    currentSortBy={sortBy}
                    currentSortDirection={sortDirection}
                    onSortByChange={handleSortByChange}
                    onSortDirectionChange={setSortDirection}
                  />
                ) : (
                  h.label
                )}
              </TableCell>
            );
          })}
          {showAccountActions && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedAccounts.map((account) => (
          <TableRow
            key={account.accountId}
            hover
            onClick={handleAccountClick(account.Organizations)}
            className="u-cursor-pointer"
          >
            {columns.map(({ value, ColumnComponent, columnAlign }) => (
              <TableCell key={value} align={columnAlign}>
                <ColumnComponent account={account} />
              </TableCell>
            ))}
            {showAccountActions && (
              <TableCell>
                <AccountItemMenu accountId={account.accountId} stopPropagation />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
